import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Modal from "./Modal";
import { categoryService } from "../../../services/api";

/**
 * Category Minimum Wages history (amount + effective month YYYY-MM).
 */
const CategoryMinimumWagesModal = ({
  darkMode,
  isOpen,
  onClose,
  category,
  canEdit = true,
  onUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [amount, setAmount] = useState("");
  const [effectiveMonth, setEffectiveMonth] = useState(
    () => new Date().toISOString().slice(0, 7)
  );
  const [notes, setNotes] = useState("");

  const load = async () => {
    if (!category?.id) return;
    try {
      setLoading(true);
      const res = await categoryService.getMinimumWages(category.id);
      setHistory(Array.isArray(res?.data?.history) ? res.data.history : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load Minimum Wages history");
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && category?.id) {
      setAmount("");
      setNotes("");
      setEffectiveMonth(new Date().toISOString().slice(0, 7));
      load();
    }
  }, [isOpen, category?.id]);

  const handleAdd = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to edit Minimum Wages.");
      return;
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error("Enter a valid Minimum Wage amount.");
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(effectiveMonth)) {
      toast.error("Select an effective month.");
      return;
    }
    try {
      setSaving(true);
      await categoryService.addMinimumWage(category.id, {
        amount: amt,
        effectiveMonth,
        notes: notes.trim() || undefined,
      });
      toast.success("Minimum wage saved");
      setAmount("");
      setNotes("");
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err?.message || "Failed to save Minimum Wage");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (mwId) => {
    if (!canEdit) return;
    if (!window.confirm("Delete this Minimum Wage history entry?")) return;
    try {
      await categoryService.deleteMinimumWage(category.id, mwId);
      toast.success("Entry deleted");
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err?.message || "Failed to delete entry");
    }
  };

  if (!isOpen || !category) return null;

  return (
    <Modal
      darkMode={darkMode}
      isOpen={isOpen}
      onClose={onClose}
      title={`Minimum Wages — ${category.name || "Category"}`}
      onCancel={onClose}
      hideSubmit
      maxWidthClass="max-w-2xl"
    >
      <div className="space-y-4">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Add a Minimum Wage amount with an effective month (month only). Past
          rows stay in the history grid for audit.
        </p>

        {canEdit && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                Amount (₹)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`w-full rounded-md border p-2 text-sm ${
                  darkMode
                    ? "border-[#4C4C4C] bg-[#2C2C2C] text-[#E0E0E0]"
                    : "border-gray-300 bg-white text-gray-900"
                }`}
                placeholder="e.g. 18000"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                Effective month
              </label>
              <input
                type="month"
                value={effectiveMonth}
                onChange={(e) => setEffectiveMonth(e.target.value)}
                className={`w-full rounded-md border p-2 text-sm ${
                  darkMode
                    ? "border-[#4C4C4C] bg-[#2C2C2C] text-[#E0E0E0]"
                    : "border-gray-300 bg-white text-gray-900"
                }`}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                Notes (optional)
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={`w-full rounded-md border p-2 text-sm ${
                  darkMode
                    ? "border-[#4C4C4C] bg-[#2C2C2C] text-[#E0E0E0]"
                    : "border-gray-300 bg-white text-gray-900"
                }`}
                placeholder="Revision note"
              />
            </div>
          </div>
        )}

        {canEdit && (
          <button
            type="button"
            disabled={saving}
            onClick={handleAdd}
            className={`rounded-md px-4 py-2 text-sm font-semibold text-white ${
              saving ? "bg-gray-400" : "bg-redhalo-red hover:opacity-90"
            }`}
          >
            {saving ? "Saving…" : "Add / Update for month"}
          </button>
        )}

        <div
          className={`overflow-hidden rounded-lg border ${
            darkMode ? "border-[#4C4C4C]" : "border-gray-200"
          }`}
        >
          <table className="w-full text-xs">
            <thead
              className={
                darkMode
                  ? "bg-[#3a3a3a] text-gray-200"
                  : "bg-gray-100 text-gray-700"
              }
            >
              <tr>
                <th className="px-2 py-2 text-left font-bold uppercase">#</th>
                <th className="px-2 py-2 text-left font-bold uppercase">
                  Effective month
                </th>
                <th className="px-2 py-2 text-right font-bold uppercase">
                  Amount (₹)
                </th>
                <th className="px-2 py-2 text-left font-bold uppercase">Notes</th>
                {canEdit && (
                  <th className="px-2 py-2 text-right font-bold uppercase">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={canEdit ? 5 : 4}
                    className="px-2 py-6 text-center text-gray-500"
                  >
                    Loading…
                  </td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td
                    colSpan={canEdit ? 5 : 4}
                    className="px-2 py-6 text-center text-gray-500"
                  >
                    No Minimum Wage history yet.
                  </td>
                </tr>
              ) : (
                history.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={`border-t ${
                      darkMode ? "border-[#4C4C4C]" : "border-gray-200"
                    }`}
                  >
                    <td className="px-2 py-2">{idx + 1}</td>
                    <td className="px-2 py-2">{row.effectiveMonth}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {Number(row.amount).toLocaleString("en-IN", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-2 py-2 text-gray-500">{row.notes || "—"}</td>
                    {canEdit && (
                      <td className="px-2 py-2 text-right">
                        <button
                          type="button"
                          className="text-red-600 hover:underline"
                          onClick={() => handleDelete(row.id)}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};

export default CategoryMinimumWagesModal;
